import { GameType } from '../types/GameType.ts';
import { getGameConfig } from '../types/GameConfig.ts';
import { getGameNumberStats, WeaverGameStats, SubtextGameStats } from './userStatsUtils.ts';
import { getRankFromWords } from '../games/subtext/progressLevels.ts';
import { SubtextSingleGameData } from './subtextGameData.ts';
import { WordPair } from './wordPairUtils.ts';

const MS_IN_DAY = 24 * 60 * 60 * 1000;
const UTC_TO_EST_MS = 5 * 60 * 60 * 1000;

export type ArchiveGameStatus = 'not-played' | 'in-progress' | 'beaten';

/**
 * Convert a calendar date (year, month 0-indexed, day) to an epoch day number,
 * matching the getNumDaysSinceEpoch() logic (EST offset).
 */
export function getEpochDayFromDate(year: number, month: number, day: number): number {
  // Create date at midnight UTC, then apply the same EST offset used in getNumDaysSinceEpoch
  const dateMs = Date.UTC(year, month, day) + UTC_TO_EST_MS;
  return Math.floor(dateMs / MS_IN_DAY);
}

/**
 * Convert an epoch day number back to a calendar date { year, month (0-indexed), day }.
 */
export function getDateFromEpochDay(epochDay: number): { year: number; month: number; day: number } {
  const ms = epochDay * MS_IN_DAY + UTC_TO_EST_MS;
  const date = new Date(ms);
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth(),
    day: date.getUTCDate(),
  };
}

/**
 * Get the game number for a given calendar date and game type.
 * Returns null if no game exists for that date.
 */
export function getGameNumberFromDate(gameType: GameType, year: number, month: number, day: number): number | null {
  const gameConfig = getGameConfig(gameType);
  const epochDay = getEpochDayFromDate(year, month, day);
  const gameNumber = epochDay - gameConfig.startDaySinceEpoch + 1;
  if (gameNumber < 1 || !gameConfig.isValidGameNumber(gameNumber)) {
    return null;
  }
  return gameNumber;
}

/**
 * Get the calendar date for a given game number.
 */
export function getDateFromGameNumber(gameType: GameType, gameNumber: number): { year: number; month: number; day: number } {
  const gameConfig = getGameConfig(gameType);
  const epochDay = gameConfig.getEpochDayFromGameNumber(gameNumber);
  return getDateFromEpochDay(epochDay);
}

/**
 * Get the archive start date (first month with games) for a game type.
 */
export function getArchiveStartDate(gameType: GameType): { year: number; month: number } {
  const { year, month } = getDateFromGameNumber(gameType, 1);
  return { year, month };
}

/**
 * Determine the archive status for a single game.
 */
export function getArchiveGameStatus(gameType: GameType, gameNumber: number): ArchiveGameStatus {
  const gameConfig = getGameConfig(gameType);

  if (gameType === GameType.SUBTEXT) {
    const stats = getGameNumberStats(gameType, gameNumber) as SubtextGameStats;
    if (!stats.words || stats.words.length === 0) {
      return 'not-played';
    }
    // Check if user reached Linguist rank
    const gameData = gameConfig.getGameData(gameNumber) as SubtextSingleGameData | null;
    if (!gameData) return 'in-progress';
    const rank = getRankFromWords(stats.words, gameData.realisticMaxScore, gameData.word, gameData.longestSubWordLength);
    if (rank.id >= 6) { // Linguist = id 6
      return 'beaten';
    }
    return 'in-progress';
  }

  // Weaver / Weaver X
  const stats = getGameNumberStats(gameType, gameNumber) as WeaverGameStats;
  if (!stats.minPathWords || stats.minPathWords.length === 0) {
    return 'not-played';
  }
  // Check if path is optimal
  const gameData = gameConfig.getGameData(gameNumber) as WordPair | null;
  if (!gameData) return 'in-progress';
  if (stats.minPathLength != null && stats.minPathLength <= gameData.optimalPathLength) {
    return 'beaten';
  }
  return 'in-progress';
}

/**
 * Get all game statuses for a given month, reading localStorage once for performance.
 * Returns a map of day (1-31) → status.
 */
export function getMonthGameStatuses(
  gameType: GameType,
  year: number,
  month: number
): Map<number, { gameNumber: number; status: ArchiveGameStatus }> {
  const result = new Map<number, { gameNumber: number; status: ArchiveGameStatus }>();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  for (let day = 1; day <= daysInMonth; day++) {
    const gameNumber = getGameNumberFromDate(gameType, year, month, day);
    if (gameNumber == null) continue;

    const gameConfig = getGameConfig(gameType);
    // Don't show future games
    if (gameNumber > gameConfig.getCurrentGameNumber()) continue;

    const status = getArchiveGameStatus(gameType, gameNumber);
    result.set(day, { gameNumber, status });
  }

  return result;
}
